(() => {
  'use strict';

  const runtimeRoot = document.documentElement;
  if (runtimeRoot.dataset.y1RuntimeReady === 'true') return;
  runtimeRoot.dataset.y1RuntimeReady = 'true';
  runtimeRoot.classList.add('y1-js');

  const mountedSections = new WeakMap();
  const mountedCarousels = new WeakSet();
  const mountedSpotlights = new WeakSet();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const modulo = (value, length) => ((value % length) + length) % length;

  function bindSpotlight(card, signal) {
    if (mountedSpotlights.has(card)) return;
    mountedSpotlights.add(card);

    let frame = 0;
    let nextX = 0;
    let nextY = 0;
    const paint = () => {
      frame = 0;
      card.style.setProperty('--mouse-x', `${nextX}px`);
      card.style.setProperty('--mouse-y', `${nextY}px`);
    };

    card.addEventListener('pointermove', (event) => {
      const rect = card.getBoundingClientRect();
      nextX = event.clientX - rect.left;
      nextY = event.clientY - rect.top;
      card.classList.add('is-spotlit');
      if (!frame) frame = requestAnimationFrame(paint);
    }, { passive: true, signal });

    card.addEventListener('pointerleave', () => {
      card.classList.remove('is-spotlit');
    }, { passive: true, signal });
  }

  function bindAllSpotlights(root, signal) {
    root.querySelectorAll('[data-y1-spotlight]').forEach((card) => bindSpotlight(card, signal));
  }

  function setupLightbox(carousel, signal, onClose) {
    const dialog = carousel.querySelector('[data-y1-lightbox]');
    const content = dialog?.querySelector('[data-y1-lightbox-content]');
    if (!dialog || !content) return;
    let opener = null;

    const clear = () => {
      content.querySelectorAll('video').forEach((video) => video.pause());
      content.replaceChildren();
      if (typeof onClose === 'function') onClose();
      if (opener?.isConnected) opener.focus({ preventScroll: true });
      opener = null;
    };

    const close = () => {
      if (dialog.open) dialog.close();
      else clear();
    };

    carousel.addEventListener('click', (event) => {
      const openButton = event.target.closest('[data-y1-media-open]');
      if (openButton && carousel.contains(openButton)) {
        const slide = openButton.closest('[data-y1-slide]');
        const source = slide?.querySelector('.y1-media-slide__media :is(video,img,svg)');
        if (!source) return;
        opener = openButton;
        carousel.querySelectorAll('video').forEach((video) => video.pause());
        const preview = source.cloneNode(true);
        preview.removeAttribute('id');
        if (preview instanceof HTMLVideoElement) {
          preview.controls = true;
          preview.autoplay = true;
          preview.playsInline = true;
        }
        content.replaceChildren(preview);
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        if (preview instanceof HTMLVideoElement) {
          const promise = preview.play();
          if (promise && typeof promise.catch === 'function') promise.catch(() => {});
        }
        return;
      }
      const closeButton = event.target.closest('[data-y1-lightbox-close]');
      if (closeButton && dialog.contains(closeButton)) close();
    }, { signal });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    }, { signal });
    dialog.addEventListener('close', clear, { signal });
  }

  function setupCarousel(carousel, signal) {
    if (mountedCarousels.has(carousel)) return;
    mountedCarousels.add(carousel);

    const slides = Array.from(carousel.querySelectorAll('[data-y1-slide]'));
    const tabs = Array.from(carousel.querySelectorAll('[data-y1-tab]'));
    const stage = carousel.querySelector('[data-y1-stage]');
    if (!slides.length || !stage) return;

    let active = Math.min(Number(carousel.dataset.y1Initial || 0), slides.length - 1);
    let touchX = null;
    let editorPaused = false;

    const setSlideFocusable = (slide, enabled) => {
      const selector = 'a[href],button,input,select,textarea,video[controls],[tabindex]';
      slide.querySelectorAll(selector).forEach((control) => {
        if (!control.hasAttribute('data-y1-original-tabindex')) {
          control.dataset.y1OriginalTabindex = control.getAttribute('tabindex') ?? '';
        }
        if (!enabled) {
          control.setAttribute('tabindex', '-1');
          return;
        }
        const original = control.dataset.y1OriginalTabindex;
        if (original === '') control.removeAttribute('tabindex');
        else control.setAttribute('tabindex', original);
      });
    };

    const syncVideos = () => {
      slides.forEach((slide, index) => {
        slide.querySelectorAll('video').forEach((video) => {
          if (index === active && carousel.dataset.y1Autoplay === 'true' && !editorPaused && !reduceMotion.matches) {
            const promise = video.play();
            if (promise && typeof promise.catch === 'function') promise.catch(() => {});
          } else {
            video.pause();
            if (index !== active) video.currentTime = 0;
          }
        });
      });
    };

    setupLightbox(carousel, signal, syncVideos);

    const goTo = (index, focusTab = false) => {
      active = modulo(index, slides.length);
      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === active);
        slide.classList.toggle('is-prev', slideIndex === modulo(active - 1, slides.length));
        slide.classList.toggle('is-next', slideIndex === modulo(active + 1, slides.length));
        slide.setAttribute('aria-hidden', slideIndex === active ? 'false' : 'true');
        slide.tabIndex = slideIndex === active ? 0 : -1;
        setSlideFocusable(slide, slideIndex === active);
      });
      tabs.forEach((tab, tabIndex) => {
        const selected = tabIndex === active;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
      });
      carousel.dataset.y1Active = String(active);
      syncVideos();
      if (focusTab && tabs[active]) tabs[active].focus({ preventScroll: true });
    };

    carousel.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-y1-action],[data-y1-index]');
      if (!trigger || !carousel.contains(trigger)) return;
      if (trigger.dataset.y1Action === 'prev') goTo(active - 1);
      if (trigger.dataset.y1Action === 'next') goTo(active + 1);
      if (trigger.dataset.y1Index !== undefined) {
        const targetIndex = Number(trigger.dataset.y1Index);
        if (targetIndex !== active) event.preventDefault();
        goTo(targetIndex);
      }
    }, { signal });

    carousel.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(active - 1, true);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(active + 1, true);
      }
    }, { signal });

    stage.addEventListener('touchstart', (event) => {
      touchX = event.changedTouches[0]?.clientX ?? null;
    }, { passive: true, signal });

    stage.addEventListener('touchend', (event) => {
      if (touchX === null) return;
      const endX = event.changedTouches[0]?.clientX ?? touchX;
      const distance = endX - touchX;
      touchX = null;
      if (Math.abs(distance) < 42) return;
      goTo(active + (distance < 0 ? 1 : -1));
    }, { passive: true, signal });

    carousel.y1GoTo = goTo;
    carousel.y1EditorSelect = (index) => {
      editorPaused = true;
      carousel.dataset.y1EditorPaused = 'true';
      goTo(index);
    };
    carousel.y1EditorDeselect = () => {
      editorPaused = false;
      carousel.dataset.y1EditorPaused = 'false';
      syncVideos();
    };
    carousel.dataset.y1EditorPaused = 'false';
    goTo(active);
  }

  function mount(section) {
    if (!section || mountedSections.has(section)) return;
    const controller = new AbortController();
    const { signal } = controller;
    bindAllSpotlights(section, signal);
    section.querySelectorAll('[data-y1-carousel]').forEach((carousel) => setupCarousel(carousel, signal));

    const observer = new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches('[data-y1-spotlight]')) bindSpotlight(node, signal);
        bindAllSpotlights(node, signal);
        if (node.matches('[data-y1-carousel]')) setupCarousel(node, signal);
        node.querySelectorAll('[data-y1-carousel]').forEach((carousel) => setupCarousel(carousel, signal));
      }));
    });
    observer.observe(section, { childList: true, subtree: true });

    mountedSections.set(section, () => {
      observer.disconnect();
      controller.abort();
      section.querySelectorAll('video').forEach((video) => video.pause());
      section.querySelectorAll('[data-y1-lightbox]').forEach((dialog) => {
        if (dialog.open) dialog.close();
        dialog.querySelector('[data-y1-lightbox-content]')?.replaceChildren();
      });
      section.querySelectorAll('[data-y1-spotlight]').forEach((card) => mountedSpotlights.delete(card));
      section.querySelectorAll('[data-y1-carousel]').forEach((carousel) => {
        mountedCarousels.delete(carousel);
        delete carousel.y1GoTo;
        delete carousel.y1EditorSelect;
        delete carousel.y1EditorDeselect;
      });
      mountedSections.delete(section);
    });
  }

  function unmount(section) {
    const cleanup = mountedSections.get(section);
    if (cleanup) cleanup();
  }

  function mountWithin(root) {
    const directSection = root.matches?.('[data-y1-section]') ? root : null;
    const nestedSections = root.querySelectorAll?.('[data-y1-section]') || [];
    if (directSection || nestedSections.length) document.body?.setAttribute('data-y1-page-active', 'true');
    if (directSection) mount(directSection);
    nestedSections.forEach(mount);
  }

  function boot() {
    const page = document.querySelector('.y1-page');
    if (!page) return;
    runtimeRoot.classList.add('y1-js');
    document.body.setAttribute('data-y1-page-active', 'true');
    mountWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => mountWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.('[data-y1-section]') ? event.target : event.target.querySelector?.('[data-y1-section]');
    if (section) unmount(section);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail?.blockId || event.target.closest?.('[data-y1-block-id]')?.dataset.y1BlockId;
    if (!blockId) return;
    document.querySelectorAll('[data-y1-carousel]').forEach((carousel) => {
      const slides = Array.from(carousel.querySelectorAll('[data-y1-slide]'));
      const index = slides.findIndex((slide) => slide.dataset.y1BlockId === blockId);
      if (index >= 0 && typeof carousel.y1EditorSelect === 'function') carousel.y1EditorSelect(index);
    });
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const blockId = event.detail?.blockId || event.target.closest?.('[data-y1-block-id]')?.dataset.y1BlockId;
    document.querySelectorAll('[data-y1-carousel]').forEach((carousel) => {
      if (!blockId || carousel.querySelector(`[data-y1-block-id="${blockId}"]`)) {
        if (typeof carousel.y1EditorDeselect === 'function') carousel.y1EditorDeselect();
      }
    });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
